import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface IWebSocketContext {
  socket: Socket | null;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  subscribe: (event: string, handler: (data: any) => void) => void;
  unsubscribe: (event: string, handler: (data: any) => void) => void;
  emit: (event: string, data: any) => void;
}

// Exported for hooks that must work outside a provider (usePauseCard in tests).
export const WebSocketContext = createContext<IWebSocketContext | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (user) {
      const serverUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_SERVER_URL || 'http://localhost:8000';
      
      setConnectionStatus('connecting');
      
      // Multiplayer: authenticate the socket handshake with the JWT so the
      // server can verify identity + authorize room joins (a teammate must not
      // be able to subscribe to a task room for an app they can't access).
      const authToken = localStorage.getItem('authToken');

      const newSocket = io(serverUrl, {
        withCredentials: true,
        transports: ['websocket', 'polling'],
        timeout: 20000,
        auth: { token: authToken },
      });

      newSocket.on('connect', () => {
        console.log('WebSocket connected:', newSocket.id);
        setIsConnected(true);
        setConnectionStatus('connected');
      });

      newSocket.on('disconnect', (reason: any) => {
        console.log('WebSocket disconnected:', reason);
        setIsConnected(false);
        setConnectionStatus('disconnected');
      });

      newSocket.on('connect_error', (error: any) => {
        console.error('WebSocket connection error:', error);
        setIsConnected(false);
        setConnectionStatus('error');
      });

      newSocket.on('reconnect', (attemptNumber: any) => {
        console.log('WebSocket reconnected after', attemptNumber, 'attempts');
        setIsConnected(true);
        setConnectionStatus('connected');
      });

      newSocket.on('reconnect_error', (error: any) => {
        console.error('WebSocket reconnection error:', error);
        setConnectionStatus('error');
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

      return () => {
        console.log('Cleaning up WebSocket connection');
        newSocket.close();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        setConnectionStatus('disconnected');
      };
    } else {
      if (socket) {
        socket.close();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        setConnectionStatus('disconnected');
      }
    }
  }, [user]);

  // Stable callbacks that always reference the current socket via ref,
  // so consumers don't get new function identities on every socket change.
  const subscribe = useCallback((event: string, handler: (data: any) => void) => {
    socketRef.current?.on(event, handler);
  }, []);

  const unsubscribe = useCallback((event: string, handler: (data: any) => void) => {
    socketRef.current?.off(event, handler);
  }, []);

  const emit = useCallback((event: string, data: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  }, []);

  const contextValue: IWebSocketContext = {
    socket,
    isConnected,
    connectionStatus,
    subscribe,
    unsubscribe,
    emit,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): IWebSocketContext => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};